import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";

const TABS = ["Cohorts", "Approvals", "Mentors", "Opportunities"];

export default function AdminEquipPage() {
    const { api } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [enterpriseId, setEnterpriseId] = useState("");
    const [cycles, setCycles] = useState([]);
    const [cycleId, setCycleId] = useState("");
    const [tab, setTab] = useState("Cohorts");

    useEffect(() => {
        api.get("/v2/admin/workspaces").then((r) => {
            const ws = r.data.workspaces || r.data || [];
            setWorkspaces(ws);
            if (ws.length && !enterpriseId) setEnterpriseId(ws[0].id);
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!enterpriseId) return;
        api.get(`/v2/admin/scoring/cycles/${enterpriseId}`).then((r) => {
            const cs = r.data.cycles || [];
            setCycles(cs);
            setCycleId(cs[0]?.id || "");
        }).catch(() => {});
    }, [api, enterpriseId]);

    return (
        <div className="p-8 max-w-6xl space-y-6" data-testid="admin-equip-page">
            <div>
                <h1 className="text-2xl font-display font-bold text-white">Equip Arm</h1>
                <p className="text-sm text-gray-500 mt-1 font-body">Auto-enrollment, mentor matching, and redeployment matching driven by cycle verdicts.</p>
            </div>

            {/* Workspace + cycle picker */}
            <div className="bg-card border border-border/40 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="equip-picker">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Workspace</span>
                <select value={enterpriseId} onChange={(e) => setEnterpriseId(e.target.value)}
                    className="bg-black border border-border/40 text-white text-sm px-3 py-1.5"
                    data-testid="equip-workspace-select">
                    {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <span className="text-xs text-gray-500 uppercase tracking-wider ml-3">Cycle</span>
                <select value={cycleId} onChange={(e) => setCycleId(e.target.value)}
                    className="bg-black border border-border/40 text-white text-sm px-3 py-1.5"
                    data-testid="equip-cycle-select">
                    {cycles.length === 0 && <option value="">No cycles</option>}
                    {cycles.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.status}</option>)}
                </select>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-border/40">
                {TABS.map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${tab === t ? "text-white border-white" : "text-gray-500 border-transparent hover:text-white"}`}
                        data-testid={`equip-tab-${t.toLowerCase()}`}>
                        {t}
                    </button>
                ))}
            </div>

            {tab === "Cohorts" && <CohortsTab enterpriseId={enterpriseId} cycleId={cycleId} />}
            {tab === "Approvals" && <ApprovalsTab enterpriseId={enterpriseId} cycleId={cycleId} />}
            {tab === "Mentors" && <MentorsTab enterpriseId={enterpriseId} cycleId={cycleId} />}
            {tab === "Opportunities" && <OpportunitiesTab enterpriseId={enterpriseId} cycleId={cycleId} />}
        </div>
    );
}

// --- Cohorts ---------------------------------------------------------------

function CohortsTab({ enterpriseId, cycleId }) {
    const { api } = useAuth();
    const [cohorts, setCohorts] = useState([]);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const load = useCallback(() => {
        if (!enterpriseId) return;
        api.get(`/v2/admin/equip/cohorts/${enterpriseId}`).then((r) => setCohorts(r.data.cohorts || [])).catch(() => {});
    }, [api, enterpriseId]);

    useEffect(() => { load(); }, [load]);

    const autoEnroll = async () => {
        if (!cycleId) { setMsg("Pick a cycle first."); return; }
        setBusy(true); setMsg("");
        try {
            const r = await api.post("/v2/admin/equip/auto-enroll", { cycle_id: cycleId });
            setMsg(`Auto-enroll → proposed ${r.data.proposed} of ${r.data.retrain_count} Retrain candidates (${r.data.already_enrolled} already enrolled).`);
            load();
        } catch (e) {
            setMsg(e?.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const updateCohort = async (cohortId, payload) => {
        try {
            await api.patch(`/v2/admin/equip/cohorts/${cohortId}`, payload);
            load();
        } catch (e) {
            setMsg(e?.response?.data?.detail || e.message);
        }
    };

    const STATUS_STYLES = {
        planning:  "border-gray-500/40 text-gray-400",
        active:    "border-emerald-500/40 text-emerald-300",
        completed: "border-teal-500/40 text-teal-300",
        cancelled: "border-red-500/40 text-red-300",
    };

    const fmtDate = (iso) => iso ? new Date(iso).toISOString().slice(0, 10) : "";

    return (
        <div className="space-y-4" data-testid="equip-cohorts-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm uppercase tracking-wider text-gray-500">Cohorts</h2>
                <button onClick={autoEnroll} disabled={busy || !cycleId}
                    className="text-xs px-3 py-1.5 border border-white/20 text-white hover:bg-white/5 disabled:opacity-40"
                    data-testid="equip-auto-enroll-btn">
                    {busy ? "Running…" : "Auto-enroll Retrain verdicts"}
                </button>
            </div>
            {msg && <p className="text-xs text-amber-300" data-testid="equip-cohorts-msg">{msg}</p>}

            <div className="grid lg:grid-cols-2 gap-4">
                {cohorts.map((c) => {
                    const next = {
                        planning:  ["active", "cancelled"],
                        active:    ["completed", "cancelled"],
                        completed: [],
                        cancelled: ["planning"],
                    }[c.status] || [];
                    return (
                        <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                            className="bg-card border border-border/40 p-5 space-y-3"
                            data-testid={`equip-cohort-${c.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-white font-semibold font-body">{c.name}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{c.duration_weeks} weeks · capacity {c.capacity}</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 border uppercase tracking-wider font-mono ${STATUS_STYLES[c.status] || "border-border/40 text-gray-400"}`}
                                    data-testid={`cohort-status-${c.id}`}>
                                    {c.status}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400">{c.description}</p>
                            <div className="flex flex-wrap gap-1.5">
                                {(c.focus_areas || []).map((f) => (
                                    <span key={f} className="text-xs px-2 py-0.5 border border-emerald-500/30 text-emerald-300 font-mono">{f}</span>
                                ))}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wider">Start</span>
                                    <input type="date" defaultValue={fmtDate(c.start_date)}
                                        onBlur={(e) => e.target.value !== fmtDate(c.start_date) && updateCohort(c.id, { start_date: e.target.value || null })}
                                        className="bg-black border border-border/40 text-white px-2 py-1"
                                        data-testid={`cohort-start-${c.id}`} />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className="text-gray-500 uppercase tracking-wider">End</span>
                                    <input type="date" defaultValue={fmtDate(c.end_date)}
                                        onBlur={(e) => e.target.value !== fmtDate(c.end_date) && updateCohort(c.id, { end_date: e.target.value || null })}
                                        className="bg-black border border-border/40 text-white px-2 py-1"
                                        data-testid={`cohort-end-${c.id}`} />
                                </label>
                            </div>
                            <div className="grid grid-cols-5 gap-1 text-xs">
                                {["proposed", "approved", "rejected", "active", "completed"].map((k) => (
                                    <div key={k} className="border border-border/40 px-2 py-1 text-center">
                                        <p className="text-gray-500 uppercase tracking-wider text-xs">{k.slice(0,3)}</p>
                                        <p className="font-mono text-white">{c.enrollment_counts?.[k] ?? 0}</p>
                                    </div>
                                ))}
                            </div>
                            {next.length > 0 && (
                                <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                                    <span className="text-xs text-gray-500 uppercase tracking-wider">Promote</span>
                                    {next.map((n) => (
                                        <button key={n} onClick={() => updateCohort(c.id, { status: n })}
                                            className={`px-3 py-1 text-xs border ${n === "cancelled" ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
                                            data-testid={`promote-${c.id}-${n}`}>
                                            → {n}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    );
                })}
                {cohorts.length === 0 && <p className="text-xs text-gray-600">No cohorts yet — run auto-enroll on a cycle with Retrain verdicts.</p>}
            </div>
        </div>
    );
}

// --- Approvals -------------------------------------------------------------

function ApprovalsTab({ enterpriseId, cycleId }) {
    const { api } = useAuth();
    const [enrollments, setEnrollments] = useState([]);
    const [filter, setFilter] = useState("proposed");
    const [busy, setBusy] = useState({});

    const load = useCallback(() => {
        if (!enterpriseId) return;
        const q = new URLSearchParams();
        if (filter) q.set("status", filter);
        if (cycleId) q.set("cycle_id", cycleId);
        api.get(`/v2/admin/equip/enrollments/${enterpriseId}?${q}`)
            .then((r) => setEnrollments(r.data.enrollments || []))
            .catch(() => {});
    }, [api, enterpriseId, filter, cycleId]);

    useEffect(() => { load(); }, [load]);

    const decide = async (id, decision) => {
        setBusy({ ...busy, [id]: true });
        try {
            await api.post(`/v2/admin/equip/enrollments/${id}/decide`, { decision, rationale: decision === "approve" ? "Manager approval" : "Manager rejection" });
            load();
        } finally { setBusy({ ...busy, [id]: false }); }
    };

    return (
        <div className="space-y-4" data-testid="equip-approvals-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm uppercase tracking-wider text-gray-500">Enrollment Queue</h2>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}
                    className="bg-black border border-border/40 text-white text-xs px-3 py-1.5"
                    data-testid="equip-approval-filter">
                    <option value="proposed">Proposed</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="">All</option>
                </select>
            </div>

            <div className="bg-card border border-border/40">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Candidate</th>
                            <th className="text-left px-4 py-2">Cohort</th>
                            <th className="text-left px-4 py-2">Source</th>
                            <th className="text-left px-4 py-2">Rationale</th>
                            <th className="text-left px-4 py-2">Status</th>
                            <th className="text-right px-4 py-2">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enrollments.map((e) => (
                            <tr key={e.id} className="border-b border-border/20" data-testid={`enrollment-row-${e.id}`}>
                                <td className="px-4 py-2 text-white">
                                    <p>{e.candidate_name}</p>
                                    <p className="text-xs text-gray-500">{e.candidate_role}</p>
                                </td>
                                <td className="px-4 py-2 text-gray-400">{e.cohort_name}</td>
                                <td className="px-4 py-2 text-gray-500 font-mono uppercase">{e.source}</td>
                                <td className="px-4 py-2 text-gray-400 max-w-md">{e.rationale}</td>
                                <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 font-mono uppercase tracking-wider ${
                                        e.status === "proposed" ? "text-amber-300 border border-amber-500/30" :
                                        e.status === "approved" ? "text-emerald-300 border border-emerald-500/30" :
                                        e.status === "rejected" ? "text-red-300 border border-red-500/30" :
                                        "text-gray-400 border border-border/40"
                                    }`}>{e.status}</span>
                                </td>
                                <td className="px-4 py-2 text-right">
                                    {e.status === "proposed" ? (
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => decide(e.id, "approve")} disabled={busy[e.id]}
                                                className="px-2 py-1 text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
                                                data-testid={`approve-${e.id}`}>Approve</button>
                                            <button onClick={() => decide(e.id, "reject")} disabled={busy[e.id]}
                                                className="px-2 py-1 text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-40"
                                                data-testid={`reject-${e.id}`}>Reject</button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-600">Decided</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {enrollments.length === 0 && (
                            <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-600">No enrollments in this state.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --- Mentors ---------------------------------------------------------------

function MentorsTab({ enterpriseId, cycleId }) {
    const { api } = useAuth();
    const [mentors, setMentors] = useState([]);
    const [matches, setMatches] = useState([]);
    const [form, setForm] = useState({ full_name: "", email: "", title: "", cadre: "Senior IC", capacity: 3, expertise: "" });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const load = useCallback(() => {
        if (!enterpriseId) return;
        api.get(`/v2/admin/equip/mentors/${enterpriseId}`).then((r) => setMentors(r.data.mentors || [])).catch(() => {});
        if (cycleId) api.get(`/v2/admin/equip/mentors/matches/${cycleId}`).then((r) => setMatches(r.data.matches || [])).catch(() => {});
    }, [api, enterpriseId, cycleId]);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true); setMsg("");
        try {
            await api.post("/v2/admin/equip/mentors", {
                enterprise_id: enterpriseId,
                full_name: form.full_name, email: form.email, title: form.title,
                cadre: form.cadre, capacity: parseInt(form.capacity, 10) || 3,
                expertise_areas: form.expertise.split(",").map((s) => s.trim()).filter(Boolean),
            });
            setForm({ full_name: "", email: "", title: "", cadre: "Senior IC", capacity: 3, expertise: "" });
            setMsg("Mentor added.");
            load();
        } catch (err) {
            setMsg(err?.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    const runMatch = async () => {
        if (!cycleId) { setMsg("Pick a cycle first."); return; }
        setBusy(true); setMsg("");
        try {
            const r = await api.post("/v2/admin/equip/mentors/match", { cycle_id: cycleId });
            setMsg(`Mentor match → matched ${r.data.matched}, skipped ${r.data.skipped}${r.data.reason ? " ("+r.data.reason+")" : ""}.`);
            load();
        } catch (err) {
            setMsg(err?.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="equip-mentors-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm uppercase tracking-wider text-gray-500">Mentor Pool</h2>
                <button onClick={runMatch} disabled={busy || !cycleId}
                    className="text-xs px-3 py-1.5 border border-white/20 text-white hover:bg-white/5 disabled:opacity-40"
                    data-testid="run-mentor-match-btn">
                    {busy ? "Matching…" : "Match Retrain candidates"}
                </button>
            </div>
            {msg && <p className="text-xs text-amber-300">{msg}</p>}

            {/* Add mentor */}
            <form onSubmit={create} className="bg-card border border-border/40 p-4 grid grid-cols-2 lg:grid-cols-6 gap-2 items-end" data-testid="add-mentor-form">
                {[
                    { k: "full_name", l: "Name" },
                    { k: "email", l: "Email" },
                    { k: "title", l: "Title" },
                ].map((f) => (
                    <div key={f.k}>
                        <label className="text-xs text-gray-500">{f.l}</label>
                        <input required={f.k !== "title"} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                            className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                            data-testid={`mentor-${f.k}`} />
                    </div>
                ))}
                <div>
                    <label className="text-xs text-gray-500">Cadre</label>
                    <select value={form.cadre} onChange={(e) => setForm({ ...form, cadre: e.target.value })}
                        className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                        data-testid="mentor-cadre">
                        <option>Apprentice</option>
                        <option>Practitioner-IC</option>
                        <option>Senior IC</option>
                        <option>Architect-track</option>
                        <option>Executive-track</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs text-gray-500">Capacity</label>
                    <input type="number" min={0} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                        className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                        data-testid="mentor-capacity" />
                </div>
                <div>
                    <button type="submit" disabled={busy}
                        className="w-full px-3 py-1.5 text-sm border border-white/20 text-white hover:bg-white/5 disabled:opacity-40"
                        data-testid="add-mentor-btn">
                        Add mentor
                    </button>
                </div>
                <div className="col-span-2 lg:col-span-6">
                    <label className="text-xs text-gray-500">Expertise (comma separated)</label>
                    <input value={form.expertise} onChange={(e) => setForm({ ...form, expertise: e.target.value })}
                        placeholder="python, ml, leadership"
                        className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                        data-testid="mentor-expertise" />
                </div>
            </form>

            {/* Mentor list */}
            <div className="bg-card border border-border/40">
                <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-border/40">Mentors ({mentors.length})</p>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Name</th>
                            <th className="text-left px-4 py-2">Title</th>
                            <th className="text-left px-4 py-2">Cadre</th>
                            <th className="text-right px-4 py-2">Capacity</th>
                            <th className="text-left px-4 py-2">Expertise</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mentors.map((m) => (
                            <tr key={m.id} className="border-b border-border/20" data-testid={`mentor-row-${m.id}`}>
                                <td className="px-4 py-2 text-white">{m.full_name} <span className="text-gray-500">·</span> <span className="text-gray-500">{m.email}</span></td>
                                <td className="px-4 py-2 text-gray-400">{m.title || "—"}</td>
                                <td className="px-4 py-2 text-gray-400">{m.cadre || "—"}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{m.capacity}</td>
                                <td className="px-4 py-2"><div className="flex flex-wrap gap-1">{(m.expertise_areas || []).map((x) => (<span key={x} className="text-xs px-1.5 py-0.5 border border-border/40 font-mono text-gray-400">{x}</span>))}</div></td>
                            </tr>
                        ))}
                        {mentors.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-600">No mentors yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Matches */}
            <div className="bg-card border border-border/40">
                <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-border/40">Matches for selected cycle ({matches.length})</p>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Candidate</th>
                            <th className="text-left px-4 py-2">Mentor</th>
                            <th className="text-right px-4 py-2">Confidence</th>
                            <th className="text-left px-4 py-2">Rationale</th>
                            <th className="text-left px-4 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matches.map((m) => (
                            <tr key={m.id} className="border-b border-border/20">
                                <td className="px-4 py-2 text-white">{m.candidate_name}<span className="text-gray-500"> · {m.candidate_role}</span></td>
                                <td className="px-4 py-2 text-white">{m.mentor_name}<span className="text-gray-500"> · {m.mentor_title || "—"}</span></td>
                                <td className="px-4 py-2 text-right font-mono text-white">{m.confidence?.toFixed?.(0) ?? "—"}</td>
                                <td className="px-4 py-2 text-gray-400 max-w-md">{m.rationale}</td>
                                <td className="px-4 py-2 text-xs uppercase tracking-wider text-gray-400">{m.status}</td>
                            </tr>
                        ))}
                        {matches.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-600">No matches yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --- Opportunities ---------------------------------------------------------

function OpportunitiesTab({ enterpriseId, cycleId }) {
    const { api } = useAuth();
    const [opps, setOpps] = useState([]);
    const [matches, setMatches] = useState([]);
    const [form, setForm] = useState({ title: "", department: "", region: "", required_capability_floor: 50, headcount: 1, description: "" });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const load = useCallback(() => {
        if (!enterpriseId) return;
        api.get(`/v2/admin/equip/opportunities/${enterpriseId}`).then((r) => setOpps(r.data.opportunities || [])).catch(() => {});
        if (cycleId) api.get(`/v2/admin/equip/opportunities/matches/${cycleId}`).then((r) => setMatches(r.data.matches || [])).catch(() => {});
    }, [api, enterpriseId, cycleId]);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true); setMsg("");
        try {
            await api.post("/v2/admin/equip/opportunities", {
                enterprise_id: enterpriseId,
                title: form.title, department: form.department, region: form.region,
                required_capability_floor: parseFloat(form.required_capability_floor) || 50,
                headcount: parseInt(form.headcount, 10) || 1,
                description: form.description,
            });
            setForm({ title: "", department: "", region: "", required_capability_floor: 50, headcount: 1, description: "" });
            setMsg("Opportunity created.");
            load();
        } catch (err) {
            setMsg(err?.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    const runMatch = async () => {
        if (!cycleId) { setMsg("Pick a cycle first."); return; }
        setBusy(true); setMsg("");
        try {
            const r = await api.post("/v2/admin/equip/opportunities/match", { cycle_id: cycleId });
            setMsg(`Redeployment match → matched ${r.data.matched}, skipped ${r.data.skipped}${r.data.reason ? " ("+r.data.reason+")" : ""}.`);
            load();
        } catch (err) {
            setMsg(err?.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="equip-opportunities-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm uppercase tracking-wider text-gray-500">Redeployment Opportunities</h2>
                <button onClick={runMatch} disabled={busy || !cycleId}
                    className="text-xs px-3 py-1.5 border border-white/20 text-white hover:bg-white/5 disabled:opacity-40"
                    data-testid="run-redeploy-match-btn">
                    {busy ? "Matching…" : "Match Redeploy verdicts"}
                </button>
            </div>
            {msg && <p className="text-xs text-amber-300">{msg}</p>}

            <form onSubmit={create} className="bg-card border border-border/40 p-4 grid grid-cols-2 lg:grid-cols-6 gap-2 items-end" data-testid="add-opportunity-form">
                {[
                    { k: "title", l: "Title", req: true },
                    { k: "department", l: "Department" },
                    { k: "region", l: "Region" },
                ].map((f) => (
                    <div key={f.k}>
                        <label className="text-xs text-gray-500">{f.l}</label>
                        <input required={f.req} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                            className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                            data-testid={`opp-${f.k}`} />
                    </div>
                ))}
                <div>
                    <label className="text-xs text-gray-500">Cap Floor</label>
                    <input type="number" min={0} max={100} value={form.required_capability_floor}
                        onChange={(e) => setForm({ ...form, required_capability_floor: e.target.value })}
                        className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                        data-testid="opp-floor" />
                </div>
                <div>
                    <label className="text-xs text-gray-500">Headcount</label>
                    <input type="number" min={1} value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })}
                        className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                        data-testid="opp-headcount" />
                </div>
                <div>
                    <button type="submit" disabled={busy}
                        className="w-full px-3 py-1.5 text-sm border border-white/20 text-white hover:bg-white/5 disabled:opacity-40"
                        data-testid="add-opportunity-btn">
                        Add opportunity
                    </button>
                </div>
                <div className="col-span-2 lg:col-span-6">
                    <label className="text-xs text-gray-500">Description</label>
                    <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full bg-black border border-border/40 text-white text-sm px-2 py-1.5"
                        data-testid="opp-description" />
                </div>
            </form>

            <div className="bg-card border border-border/40">
                <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-border/40">Opportunities ({opps.length})</p>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Title</th>
                            <th className="text-left px-4 py-2">Dept</th>
                            <th className="text-left px-4 py-2">Region</th>
                            <th className="text-right px-4 py-2">Cap Floor</th>
                            <th className="text-right px-4 py-2">Headcount</th>
                            <th className="text-left px-4 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {opps.map((o) => (
                            <tr key={o.id} className="border-b border-border/20" data-testid={`opportunity-row-${o.id}`}>
                                <td className="px-4 py-2 text-white">{o.title}</td>
                                <td className="px-4 py-2 text-gray-400">{o.department || "—"}</td>
                                <td className="px-4 py-2 text-gray-400">{o.region || "—"}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{o.required_capability_floor}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{o.headcount}</td>
                                <td className="px-4 py-2 text-xs uppercase tracking-wider text-gray-400">{o.status}</td>
                            </tr>
                        ))}
                        {opps.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-600">No opportunities yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="bg-card border border-border/40">
                <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-border/40">Matches for selected cycle ({matches.length})</p>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Candidate</th>
                            <th className="text-left px-4 py-2">Opportunity</th>
                            <th className="text-right px-4 py-2">Fit</th>
                            <th className="text-left px-4 py-2">Rationale</th>
                            <th className="text-left px-4 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matches.map((m) => (
                            <tr key={m.id} className="border-b border-border/20">
                                <td className="px-4 py-2 text-white">{m.candidate_name}<span className="text-gray-500"> · {m.candidate_role}</span></td>
                                <td className="px-4 py-2 text-white">{m.opportunity_title}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{m.fit_score?.toFixed?.(0) ?? "—"}</td>
                                <td className="px-4 py-2 text-gray-400 max-w-md">{m.rationale}</td>
                                <td className="px-4 py-2 text-xs uppercase tracking-wider text-gray-400">{m.status}</td>
                            </tr>
                        ))}
                        {matches.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-600">No matches yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
